import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ITransactionsApiService } from './transactions-api.interface';
import { environment } from '@orderna/admin-panel/src/environments/environment';
import { SuccessResponse } from '../shared/success-response';
import { FindTransactionsQueryDto } from './dtos/find-transactions.query.dto';
import { FindTransactionsResponseDto } from './dtos/find-transactions.response.dto';
import { ExportTransactionsQueryDto } from './dtos/export-transactions.query.dto';
import { handleBlobHttpResponse } from '@orderna/admin-panel/src/utils/handle-blob-http-response';
import { HandleBlobHttpResponseOptions } from '@orderna/admin-panel/src/types/handle-blob-http-response-options';
import { CreateTransactionResponseDto } from './dtos/create-transaction.response.dto';
import { CreateTransactionRequestDto } from './dtos/create-transaction.request.dto';
import { FindTransactionDetailsResponseDto } from './dtos/find-transaction-details.response.dto';
import { ParkTransactionRequestDto } from './dtos/park-transaction.request.dto';
import { ParkTransactionResponseDto } from './dtos/park-transaction.response.dto';
import { UnparkTransactionResponseDto } from './dtos/unpark-transaction.response.dto';
import { UnparkTransactionRequestDto } from './dtos/unpark-transaction.request.dto';
import { ExportExcelPdfService } from '../export-excel-pdf/export-excel-pdf.service';
import { PreviewTransactionTotalsRequestDto } from './dtos/preview-transaction-totals.request.dto';
import { PreviewTransactionTotalsResponseDto } from './dtos/preview-transaction-totals.response.dto';
import { CancelTransactionRequestDto } from './dtos/cancel-transaction.request.dto';

@Injectable({
  providedIn: 'root',
})
export class TransactionsApiService implements ITransactionsApiService {
  private readonly API_URL = `${environment.api}/transactions`;
  private http = inject(HttpClient);
  private exportService = inject(ExportExcelPdfService);

  async createTransaction(
    data: CreateTransactionRequestDto,
  ): Promise<CreateTransactionResponseDto> {
    const response = await firstValueFrom(
      this.http.post<SuccessResponse<CreateTransactionResponseDto>>(
        `${this.API_URL}`,
        data,
      ),
    );

    return response.data;
  }

  async parkTransaction(
    uuid: string,
    data: ParkTransactionRequestDto,
  ): Promise<ParkTransactionResponseDto> {
    const response = await firstValueFrom(
      this.http.patch<SuccessResponse<ParkTransactionResponseDto>>(
        `${this.API_URL}/${uuid}/park`,
        data,
      ),
    );

    return response.data;
  }

  async unparkTransaction(
    uuid: string,
    data: UnparkTransactionRequestDto,
  ): Promise<UnparkTransactionResponseDto> {
    const response = await firstValueFrom(
      this.http.patch<SuccessResponse<UnparkTransactionResponseDto>>(
        `${this.API_URL}/${uuid}/unpark`,
        data,
      ),
    );

    return response.data;
  }

  async cancelTransaction(
    uuid: string,
    data: CancelTransactionRequestDto,
  ): Promise<void> {
    await firstValueFrom(
      this.http.patch<SuccessResponse>(`${this.API_URL}/${uuid}/cancel`, data),
    );
  }

  async previewTransactionTotals(
    data: PreviewTransactionTotalsRequestDto,
  ): Promise<PreviewTransactionTotalsResponseDto> {
    const response = await firstValueFrom(
      this.http.post<SuccessResponse<PreviewTransactionTotalsResponseDto>>(
        `${this.API_URL}/preview-totals`,
        data,
      ),
    );

    return response.data;
  }

  async findTransactions(
    query: FindTransactionsQueryDto,
  ): Promise<FindTransactionsResponseDto> {
    const params = new HttpParams({ fromObject: query as any });
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindTransactionsResponseDto>>(
        `${this.API_URL}`,
        {
          params,
        },
      ),
    );

    return response.data;
  }

  async findTransactionDetails(
    uuid: string,
  ): Promise<FindTransactionDetailsResponseDto> {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindTransactionDetailsResponseDto>>(
        `${this.API_URL}/${uuid}/details`,
      ),
    );

    return response.data;
  }

  async exportTransactions(
    query: ExportTransactionsQueryDto,
    options?: HandleBlobHttpResponseOptions,
  ) {
    const params = new HttpParams({ fromObject: query as any });
    const response = await firstValueFrom(
      this.http.get(`${this.API_URL}/export`, {
        responseType: 'blob',
        observe: 'response',
        params,
      }),
    );

    handleBlobHttpResponse(response, options, this.exportService);
  }
}
